import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { CreditCard, Minus, Plus, Search, ShoppingCart, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const paymentMethods = ["Cash", "QRIS", "Transfer"];
const DEFAULT_TRANSFER_BANK = "BCA";
const selectClass =
  "rounded-sm border-0 bg-white/[0.04] px-3 text-white outline-none ring-1 ring-white/[0.035] focus:ring-white/15 [&_option]:bg-white [&_option]:text-black";

const formatRupiah = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const resolveImageUrl = (image) => {
  if (!image) return "";
  if (String(image).startsWith("http://") || String(image).startsWith("https://")) return image;
  return `${BACKEND_URL}${image}`;
};

export default function PointOfSale() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [transferBanks, setTransferBanks] = useState([DEFAULT_TRANSFER_BANK]);
  const [cart, setCart] = useState([]);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [transferBank, setTransferBank] = useState(DEFAULT_TRANSFER_BANK);
  const [isInstantOrder, setIsInstantOrder] = useState(false);
  const [sideTab, setSideTab] = useState("cart");
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(false);

  const categoryMap = useMemo(
    () =>
      categories.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {}),
    [categories]
  );

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return products
      .filter((product) => product.is_active !== false)
      .filter((product) => selectedCategory === "all" || product.category === selectedCategory)
      .filter((product) => {
        if (!normalizedQuery) return true;
        return String(product.name || "").toLowerCase().includes(normalizedQuery);
      })
      .sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [products, query, selectedCategory]);

  const cartTotal = useMemo(
    () => cart.reduce((total, item) => total + Number(item.price || 0) * Number(item.quantity || 0), 0),
    [cart]
  );

  const cartCount = useMemo(
    () => cart.reduce((total, item) => total + Number(item.quantity || 0), 0),
    [cart]
  );

  useEffect(() => {
    fetchPosData();
  }, []);

  useEffect(() => {
    if (cart.length === 0 && sideTab === "payment") {
      setSideTab("cart");
    }
  }, [cart.length, sideTab]);

  const fetchPosData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, banksRes] = await Promise.all([
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/product-categories`, { withCredentials: true }),
        axios.get(`${API}/banks`, { withCredentials: true }),
      ]);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      const activeBankNames = (Array.isArray(banksRes.data) ? banksRes.data : [])
        .filter((item) => item && item.is_active !== false)
        .map((item) => String(item.name || "").trim().toUpperCase())
        .filter(Boolean);
      const nextTransferBanks = activeBankNames.length > 0 ? activeBankNames : [DEFAULT_TRANSFER_BANK];
      setTransferBanks(nextTransferBanks);
      setTransferBank((current) => {
        const normalizedCurrent = String(current || "").trim().toUpperCase();
        if (nextTransferBanks.includes(normalizedCurrent)) return normalizedCurrent;
        return nextTransferBanks[0];
      });
    } catch (error) {
      console.error("Failed to fetch POS data:", error);
      alert(error.response?.data?.detail || "Gagal mengambil data produk POS");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const stock = Number(product.stock || 0);
    if (stock <= 0) return;
    setCart((current) => {
      const existing = current.find((item) => item.id === product.id);
      if (existing) {
        if (existing.quantity >= stock) return current;
        return current.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [
        ...current,
        {
          id: product.id,
          name: product.name,
          price: Number(product.price || 0),
          stock,
          image: product.image,
          quantity: 1,
        },
      ];
    });
    setSideTab("cart");
  };

  const updateQuantity = (productId, change) => {
    setCart((current) =>
      current
        .map((item) => {
          if (item.id !== productId) return item;
          const nextQuantity = item.quantity + change;
          if (nextQuantity <= 0) return null;
          if (nextQuantity > item.stock) return item;
          return { ...item, quantity: nextQuantity };
        })
        .filter(Boolean)
    );
  };

  const removeItem = (productId) => {
    setCart((current) => current.filter((item) => item.id !== productId));
  };

  const resetCart = () => {
    setCart([]);
    setCustomerName("");
    setCustomerPhone("");
    setPaymentMethod("Cash");
    setTransferBank(transferBanks[0] || DEFAULT_TRANSFER_BANK);
    setIsInstantOrder(false);
  };

  const buildCheckoutPayload = () => ({
    customer_name: customerName.trim() || "Walk-in Customer",
    customer_phone: customerPhone.trim(),
    payment_method: paymentMethod,
    transfer_bank: transferBank,
    instant_order: isInstantOrder,
    items: cart.map((item) => ({
      product_id: item.id,
      quantity: item.quantity,
    })),
  });

  const checkout = async () => {
    if (cart.length === 0) {
      alert("Keranjang masih kosong.");
      return;
    }

    try {
      setCheckingOut(true);
      await axios.post(`${API}/pos/orders`, buildCheckoutPayload(), { withCredentials: true });
      resetCart();
      await fetchPosData();
      alert("Transaksi POS berhasil disimpan.");
    } catch (error) {
      console.error("Failed to create POS order:", error);
      alert(error.response?.data?.detail || "Gagal memproses transaksi POS");
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <DashboardLayout>
      <Header title="Point of Sale" />
      <div className="flex h-[calc(100vh-4rem)] flex-col overflow-hidden p-4" data-testid="pos-page">
        <div className="mb-3 shrink-0 flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="mb-1 inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#F3D77A]">
              <ShoppingCart className="h-3.5 w-3.5" />
              POS Kasir
            </div>
            <h2 className="text-lg font-semibold text-white">Transaksi produk langsung di admin</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              Pilih produk dari Master Product, masukkan ke keranjang, lalu proses pembayaran.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={fetchPosData}
            className="h-9 rounded-sm border-transparent bg-white/[0.03] text-white ring-1 ring-white/[0.04] hover:bg-white/[0.06]"
          >
            Refresh Produk
          </Button>
        </div>

        <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <section className="flex min-h-0 flex-col rounded-sm bg-zinc-950/40 p-3 ring-1 ring-white/[0.04]">
            <div className="mb-3 shrink-0 grid gap-2 lg:grid-cols-[minmax(0,1fr)_200px]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Cari produk..."
                  className="h-9 rounded-sm border-transparent bg-white/[0.04] pl-10 text-sm text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className={`h-9 text-sm ${selectClass}`}
              >
                <option value="all">Semua Kategori</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {loading ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="mx-auto h-7 w-7 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
              </div>
            ) : filteredProducts.length > 0 ? (
              <div className="min-h-0 flex-1 overflow-y-auto pr-1">
                <div className="grid gap-2 sm:grid-cols-2 2xl:grid-cols-3">
                {filteredProducts.map((product) => {
                  const stock = Number(product.stock || 0);
                  const isOutOfStock = stock <= 0;
                  return (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => addToCart(product)}
                      disabled={isOutOfStock}
                      className="group flex gap-2 rounded-sm bg-white/[0.025] p-2.5 text-left ring-1 ring-white/[0.035] transition-colors hover:bg-white/[0.045] disabled:cursor-not-allowed disabled:opacity-45"
                    >
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-sm bg-zinc-900">
                        {product.image ? (
                          <img src={resolveImageUrl(product.image)} alt={product.name} className="h-full w-full object-cover" />
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold leading-tight text-white">{product.name}</p>
                        <p className="mt-1 text-xs text-zinc-500">{categoryMap[product.category] || product.category}</p>
                        {String(product.sale_type || "OWN").toUpperCase() === "CONSIGNMENT" ? (
                          <p className="mt-1 inline-flex rounded-sm bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-sky-300">
                            Titipan
                          </p>
                        ) : null}
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-[#F3D77A]">{formatRupiah(product.price)}</p>
                          <Badge variant="outline" className="rounded-sm border-transparent bg-white/[0.04] text-[10px] text-zinc-300">
                            Stok {stock}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  );
                })}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center rounded-sm bg-white/[0.02] px-4 text-center text-sm text-zinc-500 ring-1 ring-white/[0.04]">
                Produk tidak ditemukan.
              </div>
            )}
          </section>

          <aside className="flex min-h-0 flex-col overflow-hidden rounded-sm bg-zinc-950/50 ring-1 ring-white/[0.04]">
            <div className="flex min-h-0 flex-1 flex-col p-2.5 pb-0">
            <div className="mb-1.5 shrink-0 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">Keranjang</h3>
                <p className="mt-0.5 text-[11px] text-zinc-500">{cartCount} item dipilih</p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={resetCart}
                disabled={cart.length === 0}
                className="h-8 rounded-sm border-transparent bg-white/[0.03] text-xs text-white ring-1 ring-white/[0.04] hover:bg-white/[0.06]"
              >
                Reset
              </Button>
            </div>

            <div className="mb-2 grid shrink-0 grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSideTab("cart")}
                className={`flex h-9 items-center justify-center gap-2 rounded-sm text-xs font-semibold transition-colors ${
                  sideTab === "cart"
                    ? "bg-[#D4AF37] text-black"
                    : "bg-white/[0.03] text-zinc-300 ring-1 ring-white/[0.04] hover:bg-white/[0.06]"
                }`}
              >
                <ShoppingCart className="h-4 w-4" />
                Keranjang
              </button>
              <button
                type="button"
                onClick={() => {
                  if (cart.length === 0) return;
                  setSideTab("payment");
                }}
                disabled={cart.length === 0}
                className={`flex h-9 items-center justify-center gap-2 rounded-sm text-xs font-semibold transition-colors ${
                  sideTab === "payment"
                    ? "bg-[#D4AF37] text-black"
                    : cart.length === 0
                    ? "cursor-not-allowed bg-white/[0.02] text-zinc-600 ring-1 ring-white/[0.035]"
                    : "bg-white/[0.03] text-zinc-300 ring-1 ring-white/[0.04] hover:bg-white/[0.06]"
                }`}
              >
                <CreditCard className="h-4 w-4" />
                Pembayaran
              </button>
            </div>

            {sideTab === "cart" ? (
            <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
              {cart.length > 0 ? (
                <>
                  {cart.map((item) => (
                    <div key={item.id} className="rounded-sm bg-white/[0.025] px-2.5 py-1 ring-1 ring-white/[0.035]">
                      <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-white">{item.name}</p>
                        <p className="text-[10px] text-zinc-500">
                          {item.quantity} x {formatRupiah(item.price)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="rounded-sm p-1 text-zinc-500 hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      </div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, -1)}
                          className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/[0.04] text-white hover:bg-white/[0.08]"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-xs font-semibold text-white">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.id, 1)}
                          className="flex h-5 w-5 items-center justify-center rounded-sm bg-white/[0.04] text-white hover:bg-white/[0.08]"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <p className="text-xs font-semibold text-white">{formatRupiah(item.price * item.quantity)}</p>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="rounded-sm bg-white/[0.02] px-4 py-4 text-center text-sm text-zinc-500 ring-1 ring-white/[0.04]">
                  Keranjang masih kosong.
                </div>
              )}
            </div>
            ) : null}

            {sideTab === "payment" ? (
            <div className="min-h-0 flex-1 overflow-hidden border-t border-white/[0.04] pt-1.5">
              <div className="grid gap-1.5">
                <div className="space-y-0.5">
                  <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Nama Customer</Label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Walk-in Customer"
                    className="h-7 rounded-sm border-transparent bg-white/[0.04] text-xs text-white ring-1 ring-white/[0.035] focus-visible:ring-white/15"
                  />
                </div>
                <div className={`grid gap-1.5 ${paymentMethod === "Transfer" ? "grid-cols-2" : "grid-cols-1"}`}>
                  <div className="space-y-0.5">
                    <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Metode Bayar</Label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => {
                        const nextMethod = e.target.value;
                        setPaymentMethod(nextMethod);
                        if (nextMethod === "Transfer") {
                          setTransferBank((current) => {
                            const normalizedCurrent = String(current || "").trim().toUpperCase();
                            if (transferBanks.includes(normalizedCurrent)) return normalizedCurrent;
                            return transferBanks[0] || DEFAULT_TRANSFER_BANK;
                          });
                        }
                      }}
                      className={`h-7 w-full text-xs ${selectClass}`}
                    >
                      {paymentMethods.map((method) => (
                        <option key={method} value={method}>
                          {method}
                        </option>
                      ))}
                    </select>
                  </div>
                  {paymentMethod === "Transfer" && (
                  <div className="space-y-0.5">
                    <Label className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Bank Transfer</Label>
                    <select
                      value={transferBank}
                      onChange={(e) => setTransferBank(e.target.value)}
                      className={`h-7 w-full text-xs ${selectClass}`}
                    >
                      {transferBanks.map((bank) => (
                        <option key={bank} value={bank}>
                          {bank}
                        </option>
                      ))}
                    </select>
                  </div>
                  )}
                </div>
                <label
                  className={`instant-order-card flex cursor-pointer items-start gap-2 rounded-sm px-2.5 py-1.5 ring-1 transition-colors ${
                    isInstantOrder
                      ? "bg-[#D4AF37]/10 ring-[#D4AF37]/35"
                      : "bg-white/[0.02] ring-white/[0.06] hover:bg-white/[0.04]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isInstantOrder}
                    onChange={(e) => setIsInstantOrder(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-white/25 bg-white/[0.04] accent-[#D4AF37]"
                  />
                  <span className="min-w-0">
                    <span className="block text-xs font-medium text-white instant-order-title">Instant</span>
                    <span className="instant-order-desc mt-0.5 block truncate text-[10px] leading-snug text-zinc-400">
                      Aktif = Completed, nonaktif = Pending.
                    </span>
                  </span>
                </label>
              </div>
            </div>
            ) : null}
            </div>
            <div className="shrink-0 border-t border-white/[0.06] bg-zinc-950/95 p-2 shadow-[0_-10px_28px_rgba(0,0,0,0.35)]">
              <div className="mb-1 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">Total Bayar</p>
                  <p className="text-lg font-semibold leading-none text-white">{formatRupiah(cartTotal)}</p>
                </div>
                <p className="text-xs text-zinc-500">{cartCount} item</p>
              </div>
              {sideTab === "cart" ? (
                <Button
                  type="button"
                  onClick={() => setSideTab("payment")}
                  disabled={cart.length === 0}
                  className="h-8 w-full rounded-sm bg-white/[0.06] text-sm font-semibold text-white hover:bg-white/[0.1] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Lanjut Pembayaran
                </Button>
              ) : (
              <Button
                type="button"
                onClick={checkout}
                disabled={cart.length === 0 || checkingOut}
                className="h-8 w-full rounded-sm bg-[#D4AF37] text-sm font-semibold text-black hover:bg-[#C59A27]"
              >
                {checkingOut
                  ? "Memproses..."
                  : paymentMethod === "QRIS"
                  ? "Simpan Pembayaran QRIS"
                  : paymentMethod === "Transfer"
                  ? "Simpan Pembayaran Transfer"
                  : "Bayar Sekarang"}
              </Button>
              )}
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}
